/**
 * The oracle's numbers, beside the kitchen it played.
 *
 * Every row comes from `summary.ts`; this lays them out. The baseline is one
 * of those rows (its score beside the Oracle's), not a second panel. The headline rows,
 * any notes and the caveat are always in view. Everything else (the budget,
 * the other rows and both tables) sits under a native `<details>` that starts
 * closed at every width, so it stays in the DOM and opens with a tap, a click,
 * Enter or Space. On a wide screen the panel sits right of the stage; below
 * 900 px it drops under it.
 */

import type { OracleStatus, OracleSummary } from '@overcooked/shared'
import type { SummaryRow } from './summary'
import {
  HEADLINE_ROWS,
  ORACLE_CAVEAT,
  chefName,
  chefTable,
  compositeTable,
  statusLabel,
  summaryNotes,
  summaryRows,
} from './summary'

function Rows({ rows }: { rows: SummaryRow[] }) {
  if (rows.length === 0) return null
  return (
    <dl className="oracle-rows">
      {rows.map((row) => (
        <div key={row.id} className="oracle-row" data-row={row.id}>
          <dt>{row.label}</dt>
          <dd>
            {row.value}
            {row.staff && (
              <>
                {' '}
                <small className="oracle-row-staff">{row.staff}</small>
              </>
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function MetricsPanel({
  summary,
  status,
  headerContentHash,
  dishLabel,
}: {
  summary: OracleSummary | null
  status: OracleStatus | null
  headerContentHash: string | null
  dishLabel: (id: string) => string
}) {
  const rows = summary?.result ? summaryRows(summary.result, dishLabel, summary.baseline) : []
  // In the order HEADLINE_ROWS names them, not the order they were built in.
  const headline = HEADLINE_ROWS.flatMap((id) => rows.filter((row) => row.id === id))
  const rest = rows.filter((row) => !HEADLINE_ROWS.includes(row.id))
  const notes = summary ? summaryNotes(summary, headerContentHash) : []
  const metrics = summary?.result?.metrics ?? null
  const chefs = metrics ? chefTable(metrics) : null
  const composite = metrics ? compositeTable(metrics.difficultyBasis) : null

  return (
    <aside className="oracle-metrics admin-panel" data-testid="oracle-metrics">
      <header className="oracle-metrics-head">
        <h2>Oracle</h2>
        {status && (
          <span className="badge oracle-status" data-status={status}>
            {statusLabel(status)}
          </span>
        )}
      </header>
      <div className="oracle-metrics-body">
        <Rows rows={headline} />
        {notes.map((note) => (
          <p key={note} className="notice oracle-note">
            {note}
          </p>
        ))}
        <p className="hint oracle-caveat" data-testid="oracle-caveat">
          {ORACLE_CAVEAT}
        </p>
        {summary && (
          <details className="oracle-details">
            <summary>Details</summary>
            <div className="oracle-details-body">
              <p className="hint oracle-budget">{summary.budgetLabel}</p>
              <Rows rows={rest} />
              {chefs && (
                <table className="oracle-table oracle-chefs" data-testid="oracle-chefs">
                  <caption>Per chef, in moves</caption>
                  <thead>
                    <tr>
                      <th scope="col" />
                      {chefs.players.map((p) => (
                        <th scope="col" key={p}>
                          {chefName(p)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chefs.rows.map((row) => (
                      <tr key={row.id}>
                        <th scope="row">{row.label}</th>
                        {row.values.map((value, i) => (
                          <td key={chefs.players[i]} data-chef={chefs.players[i]} data-metric={row.id}>
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {composite && (
                <table className="oracle-table oracle-composite" data-testid="oracle-composite">
                  <caption>
                    Difficulty, term by term, {composite.against}{' '}
                    <small className="oracle-row-staff" data-testid="oracle-reference">
                      {composite.reference}
                    </small>
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Term</th>
                      <th scope="col">Value</th>
                      <th scope="col">
                        Vs. typical <small className="oracle-row-staff">(z)</small>
                      </th>
                      <th scope="col">Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {composite.rows.map((row) => (
                      <tr key={row.id} data-term={row.id}>
                        <th scope="row">{row.label}</th>
                        <td>{row.value}</td>
                        <td>{row.z}</td>
                        <td>{row.weight}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </details>
        )}
      </div>
    </aside>
  )
}
